import { DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  SaveButton,
  TextInput,
  SimpleForm,
  useDataProvider,
  useNotify,
  useRefresh,
  NumberInput,
  SelectInput,
  DateTimeInput,
  ArrayInput,
  SimpleFormIterator,
  useTranslate,
} from 'react-admin';
import dayjs from 'dayjs';
import { getCategoryChoices } from '../../categories/service';

export default function CreateConsecutiveCheckinTask({ onClose }: { onClose: () => void }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [saving, setSaving] = useState(false);
  const [categoryChoices, setCategoryChoices] = useState<Array<{ id: number | string; name: string }>>([]);
  const verificationMethodChoices = [
    { id: 'AUTO', name: 'AUTO' },
    { id: 'MANUAL', name: 'MANUAL' },
    { id: 'CLICK_VERIFY', name: 'CLICK_VERIFY' },
  ];

  useEffect(() => {
    getCategoryChoices().then(setCategoryChoices).catch(() => setCategoryChoices([]));
  }, []);

  const onSubmit = async (values: any) => {
    setSaving(true);
    try {
      const normalizedMilestones = Array.isArray(values.milestones)
        ? values.milestones.map((m: any) => {
          const nameFromGroup = m?.name || {};
          const mergedName = {
            en: m?.['name.en'] ?? nameFromGroup?.en ?? '',
            zh: m?.['name.zh'] ?? nameFromGroup?.zh ?? undefined,
            ja: m?.['name.ja'] ?? nameFromGroup?.ja ?? undefined,
            hk: m?.['name.hk'] ?? nameFromGroup?.hk ?? undefined,
            vi: m?.['name.vi'] ?? nameFromGroup?.vi ?? undefined,
            hi: m?.['name.hi'] ?? nameFromGroup?.hi ?? undefined,
          } as any;
          return {
            days: Number(m?.days) || 0,
            points: Number(m?.points) || 0,
            taskIcon: m?.taskIcon ?? undefined,
            name: mergedName,
          };
        })
        : [];

      const processedValues = {
        localType: 'CONSECUTIVE_CHECKIN',
        ...values,
        name: typeof values.name === 'object' ? values.name : { en: values.name || '' },
        points: Number(values.points) || 0,
        isActive: Boolean(values.isActive),
        startDate: values.startDate && values.startDate !== '' ? dayjs(values.startDate).valueOf() : null,
        endDate: values.endDate && values.endDate !== '' ? dayjs(values.endDate).valueOf() : null,
        milestones: normalizedMilestones,
      };

      await dataProvider.create('activity-task', { data: processedValues });
      notify(translate('activity.tasks.createdSuccessfully'), { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      console.error('Create error:', error);
      notify(translate('activity.tasks.failedToCreate'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogTitle>{translate('activity.common.createTask')}</DialogTitle>
      <DialogContent>
        <SimpleForm onSubmit={onSubmit} toolbar={<SaveButton alwaysEnable disabled={saving} />}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="h6">{translate('activity.common.basicInformation')}</Typography>
            <SelectInput
              source="categoryId"
              label={translate('activity.common.category')}
              fullWidth
              required
              choices={categoryChoices}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {translate('activity.common.nameMultiLanguage')}
            </Typography>

            <TextInput source="name.en" label={translate('activity.name.en')} fullWidth required />
            <TextInput source="name.zh" label={translate('activity.name.zh')} fullWidth />
            <TextInput source="name.ja" label={translate('activity.name.ja')} fullWidth />
            <TextInput source="name.hk" label={translate('activity.name.hk')} fullWidth />
            <TextInput source="name.vi" label={translate('activity.name.vi')} fullWidth />
            <TextInput source="name.hi" label={translate('activity.name.hi')} fullWidth />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {translate('activity.common.milestones')}
            </Typography>
            <ArrayInput source="milestones" label="" fullWidth>
              <SimpleFormIterator>
                <NumberInput source="days" label={translate('activity.common.days')} fullWidth required />
                <NumberInput source="points" label={translate('activity.common.points')} fullWidth required />
                <TextInput source="taskIcon" label={translate('activity.tasks.taskIcon')} />
                <TextInput source="name.en" label={translate('activity.name.en')} />
                <TextInput source="name.zh" label={translate('activity.name.zh')} />
                <TextInput source="name.ja" label={translate('activity.name.ja')} />
                <TextInput source="name.hk" label={translate('activity.name.hk')} />
                <TextInput source="name.vi" label={translate('activity.name.vi')} />
                <TextInput source="name.hi" label={translate('activity.name.hi')} />
              </SimpleFormIterator>
            </ArrayInput>

            <Typography variant="h6" sx={{ mt: 2 }}>
              {translate('activity.common.additionalFields')}
            </Typography>

            <TextInput source="description" label={translate('activity.common.description')} fullWidth multiline rows={3} />

            <Stack direction="row" alignItems="center" spacing={2}>
              <TextInput source="actionTarget" label={translate('activity.tasks.actionTarget')} fullWidth />
              <Typography variant="body2" color="text.secondary">
                *AccumulatedTradingTasks with actionTarget = memeTrade and frequency = PROGRESSIVE
              </Typography>
            </Stack>

            <SelectInput
              source="verificationMethod"
              label={translate('activity.tasks.verificationMethod')}
              fullWidth
              choices={verificationMethodChoices}
            />

            <TextInput source="taskIcon" label={translate('activity.tasks.taskIcon')} fullWidth />
            <TextInput source="buttonText" label={translate('activity.tasks.buttonText')} fullWidth />
            <DateTimeInput source="startDate" label={translate('activity.common.startDate')} fullWidth />
            <DateTimeInput source="endDate" label={translate('activity.common.endDate')} fullWidth />
            <NumberInput source="sortOrder" label={translate('activity.common.sortOrder')} fullWidth defaultValue={0} />
          </Stack>
        </SimpleForm>
      </DialogContent>
    </>
  );
}
